<template>
  <MtxPage width="md">
    <template #header>
      <h1>{{ isSubmission ? "Create Submission" : "First to Hash" }}</h1>
      <i v-if="!isSubmission">Timestamp your invention on the blockchain for immutable proof</i>
    </template>

    <MtxCard v-if="createTxHash" class="text-center">
      <h3>{{ isSubmission ? "Submission" : "First to Hash Timestamp" }} Submitted!</h3>
      <p>
        You have successfully submitted your
        {{ isSubmission ? "submission" : "timestamped files" }} to the network. It is currently
        waiting to be mined and depending on network congestion will complete between seconds and
        several minutes.
      </p>
      <p>
        Learn more about blockchain transactions
        <RouterLink to="/about#why-do-transactions-take-long">here.</RouterLink>
        <!-- TODO link to  transactions FAQ -->
      </p>

      <TxProgress class="my-2" :tx="createTxHash" />

      <RouterLink to="/tournaments" class="mx-2">Go to Tournaments</RouterLink>
      <button @click="resetForm" class="btn--link mx-2">Create Another</button>
    </MtxCard>

    <MtxCard v-else>
      <p>
        Timestamping and submitting your content on the Matryx Platform is an easy three-step
        process:
      </p>
      <ol>
        <li>Upload your files (or provide an IPFS Hash)</li>
        <li>Claim your files' unique content ID</li>
        <li>Officially publish your content on the Matryx Platform</li>
      </ol>
      <p>
        This process allows you to upload and timestamp your content in a very secure manner. Your
        time-stamped content is also known as a Matryx Commit.
      </p>
      <p class="mb-3">
        <strong>*Note:</strong>
        All files uploaded are publicly accessible through IPFS. You can find out more about IPFS
        <RouterLink to="/about#what-is-matryx">here.</RouterLink>
        <!-- TODO link to IPFS FAQ -->
      </p>

      <MtxToggle label="1. Upload Your Files" ref="step1" startOpen class="mb-3">
        <MtxUpload
          v-model="ipfsHash"
          @loading="onFileLoading"
          @update="onFileUpdate"
          label="IPFS Hash"
          required
        />
        <small v-if="$v.ipfsHash.$error" class="color-red">Must be a valid IPFS hash.</small>
        <small v-else-if="commitExists" class="color-red">
          Commit already created from this content
        </small>
      </MtxToggle>

      <MtxToggle
        label="2. Claim Your Unique Content ID"
        ref="step2"
        :disabled="$v.ipfsHash.$invalid || commitExists !== false"
        class="mb-3 text-center"
      >
        <template v-if="!claimTxHash">
          <p>
            <!-- TODO -->
            You are about to claim
            <br />
            {{ ipfsHash }}
          </p>
          <MtxInput
            v-model="isReady"
            type="checkbox"
            label="I am ready to claim this hash"
            class="mb-2"
          />
        </template>

        <p v-else>
          Claim submitted for
          <br />
          {{ ipfsHash }}
          <br />
          Waiting for transaction to mine...
          <TxProgress :tx="claimTxHash" @success="onClaimSuccess" class="my-2" />
        </p>

        <button :class="{ 'btn--disabled': !canClaim }" @click="requireWeb3(onClaim)">
          Claim IPFS Hash
        </button>
      </MtxToggle>

      <MtxToggle
        :label="
          `3. Publish ${isSubmission ? 'Submission to Tournament' : 'Timestamp on Matryx Platform'}`
        "
        ref="step3"
        :disabled="!(claimedHash && ipfsHash === claimedHash)"
      >
        <form @submit.prevent>
          <p>
            Congrats! You have claimed your files' unique ID. Now it is time to officially publish
            your timestamp on the Ethereum Blockchain.
          </p>

          <p>
            Each unique invention has value; define your content's value in MTX. Typically it ranges
            between 1 and 100. You can learn more details about this
            <RouterLink to="/about#what-is-commit-value">here.</RouterLink>
            <!-- TODO link to commit value FAQ -->
          </p>

          <div class="grid grid-gap grid-2-columns@md align-center mb-2">
            <MtxInput
              v-model="value"
              :validation="$v.value"
              :min="$v.value.$params.min.min"
              label="Commit Value (MTX)"
              type="number"
              step="any"
              required
            />

            <MtxInput
              v-model="hasParent"
              label="This builds off of another commit"
              type="checkbox"
              class="mt-2@md"
            />
          </div>

          <MtxInput
            v-model="parent"
            v-if="hasParent"
            :validation="$v.parent"
            label="Commit Parent Hash"
            description="What is the commit hash that you are iterating on top of?"
            class="mb-2"
          />

          <template v-if="isSubmission">
            <MtxInput
              v-model="submission.title"
              :validation="$v.submission.title"
              label="Submission Title"
              class="mb-2"
              required
            />

            <MtxInput
              v-model="submission.description"
              :validation="$v.submission.description"
              label="Submission Description"
              type="textarea"
              class="mb-2"
              required
            />
          </template>

          <div class="text-center">
            <button
              type="submit"
              :class="{ 'btn--disabled': isUploading || isLoading || $v.$invalid || !claimedHash }"
              @click="requireWeb3(onCreate)"
            >
              {{ !web3.ready ? "Sign in to create" : "Submit" }}
            </button>
          </div>
        </form>
      </MtxToggle>
    </MtxCard>
  </MtxPage>
</template>

<script>
import {
  required,
  requiredIf,
  maxLength,
  minValue,
  decimal,
  ipfsHash,
  shaHash,
  ethAddress
} from "@/utils/validators"
import API from "@/api"
import exitGuard from "@/mixins/exitGuard"
import web3 from "@/mixins/web3"
import errorLogger from "@/utils/errorLogger"

function random() {
  return Array(32)
    .fill()
    .map(() => Math.floor(36 * Math.random()).toString(36))
    .join("")
}

export default {
  mixins: [exitGuard, web3],

  props: {
    tournamentAddress: String
  },

  data: () => ({
    isUploading: false,
    isLoading: false,
    isReady: false,
    isSubmission: false,
    salt: random(),
    ipfsHash: "",
    commitExists: false,
    claimedHash: "",
    claimTxHash: null,
    createTxHash: null,
    hasParent: false,
    parent: "",
    value: "",
    submission: {
      tournament: "",
      title: "",
      description: ""
    }
  }),

  metaInfo: {
    titleTemplate: "%s | Create Commit",
    meta: [
      {
        name: "description",
        content: "Be the 'First to Hash' any content to the Matryx Platform by creating a commit."
      },
      {
        property: "og:description",
        content: "Be the 'First to Hash' any content to the Matryx Platform by creating a commit."
      }
      // TODO
      // {
      //   property: "og:image",
      //   content: "URL HERE"
      // },
    ]
  },

  computed: {
    canClaim() {
      return this.isReady && !this.isLoading && this.ipfsHash !== this.claimedHash
    },

    preventExit() {
      // prevent exit if any submission fields filled out
      const { title, description } = this.submission
      return [this.parent, title, description].some(x => x)
    }
  },

  mounted() {
    const tournament = this.tournamentAddress
    this.isSubmission = !!tournament && ethAddress(tournament)

    const lastClaim = JSON.parse(localStorage.getItem("mtx-last-claim"))
    if (lastClaim) {
      this.ipfsHash = lastClaim.ipfsHash
      this.claimTxHash = lastClaim.claimTxHash
      this.salt = lastClaim.salt
      this.setStep(2)
    }

    if (this.isSubmission) {
      this.submission.tournament = tournament
    }
  },

  created() {
    this.requireWeb3()
  },

  methods: {
    onFileLoading(isUploading) {
      this.isUploading = isUploading
      this.commitExists = null
      this.$v.ipfsHash.$reset()
    },

    async onFileUpdate(ipfsHash) {
      if (!ipfsHash || this.$v.ipfsHash.$invalid) return

      this.claimTxHash = null
      this.isReady = false
      localStorage.removeItem("mtx-last-claim")

      const Commit = await this.contracts.Commit
      const commit = await Commit.getCommitByContent(ipfsHash)

      if (commit) {
        this.commitExists = true
        return
      }

      this.commitExists = false
      setTimeout(() => this.setStep(2), 300)
    },

    async onClaim() {
      this.$v.ipfsHash.$touch()
      if (!this.canClaim) return

      const Commit = await this.contracts.Commit

      const userAddress = this.web3.account
      const data = {
        userAddress,
        salt: this.salt,
        ipfsHash: this.ipfsHash
      }

      this.isLoading = true
      const tx = this.addPlannedTx({
        txFn: () => Commit.claimCommit(...Object.values(data)),
        header: "Claim Commit"
      })

      try {
        const { hash } = await tx
        this.claimTxHash = hash
        localStorage.setItem("mtx-last-claim", JSON.stringify({ ...data, claimTxHash: hash }))
        this.isReady = false
      } finally {
        this.$v.ipfsHash.$reset()
        this.isLoading = false
      }
    },

    onClaimSuccess() {
      this.claimedHash = this.ipfsHash
      setTimeout(() => this.setStep(3), 300)
    },

    async onCreate() {
      this.$v.$touch()
      if (this.$v.$invalid || this.isLoading) return

      this.isLoading = true

      try {
        const Commit = await this.contracts.Commit

        let txFn
        let params = [
          this.parent,
          false, // isFork
          this.salt,
          this.ipfsHash,
          this.value
        ]

        if (this.isSubmission) {
          const upload = API.uploadJson({
            title: this.submission.title,
            description: this.submission.description
          })

          this.$web3.$emit("upload", upload)
          const { success, data } = await upload

          if (!success) throw new Error("Unknown error uploading JSON to API")

          params = [this.submission.tournament, data.hash, ...params]
          txFn = () => Commit.createSubmission(...params)
        } else {
          txFn = () => Commit.createCommit(...params)
        }

        const tx = this.addPlannedTx({
          txFn,
          header: `Create ${this.isSubmission ? "Submission" : "Commit"}`
        })

        const { hash } = await tx

        this.parent = ""
        this.submission.title = ""
        this.submission.description = ""

        localStorage.removeItem("mtx-last-claim")
        this.createTxHash = hash
      } catch (error) {
        if (error.message.includes("User denied transaction signature")) return

        errorLogger.log(error)
        this.$notify({
          title: "Error",
          type: "error",
          text: "There was an error creating the commit. Please try again later."
        })
      } finally {
        this.isLoading = false
      }
    },

    setStep(step) {
      for (let i = 1; i <= 3; i++) {
        this.$refs[`step${i}`].setOpen(i === step)
      }
    },

    resetForm() {
      this.$v.$reset()
      this.salt = random()
      this.ipfsHash = ""
      this.claimedHash = ""
      this.claimTxHash = null
      this.createTxHash = null
      this.hasParent = false
      this.parent = ""
      this.value = ""
      this.submission.title = ""
      this.submission.description = ""
    }
  },

  validations() {
    return {
      ipfsHash: {
        required,
        hash: ipfsHash
      },
      parent: {
        hash: shaHash
      },
      value: {
        required,
        decimal,
        min: minValue(1e-18)
      },
      submission: {
        tournament: {
          required: requiredIf(() => this.isSubmission),
          address: ethAddress
        },
        title: {
          required: requiredIf(() => this.isSubmission),
          maxLength: maxLength(255)
        },
        description: {
          required: requiredIf(() => this.isSubmission),
          maxLength: maxLength(2000)
        }
      }
    }
  }
}
</script>
